/* @jsx mdx */
import React from 'react';
import { mdx } from '@mdx-js/react'
/* @jsxRuntime classic */
/* @jsx mdx */

export const metadata = {
  title: "Wow, Emacs is Rough!"
};

const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`I `}<a parentName="p" {...{
        "href": "/post/2021-01-08-wow-emacs"
      }}>{`switched to Emacs back in January`}</a>{` of this year. I was excited about how extensible it is and I can see why people have stuck with the editor for so long.`}</p>
    <p>{`However, my honeymoon with Emacs didn't last very long. I took away some lessons, bought an upgrade to Sublime Text 4, and now have the best editor experience I've ever had.`}</p>
    <p>{`A lot of my experience mirrors that of `}<a parentName="p" {...{
        "href": "https://thume.ca/2017/03/04/my-text-editor-journey-vim-spacemacs-atom-and-sublime-text/"
      }}>{`this blog post from Tristan Hume`}</a>{`. I found it while looking for sympathy during the peak of my frustration with Emacs and it really helped me feel like it wasn't my fault Emacs wasn't working for me.`}</p>
    <h2>{`The Good`}</h2>
    <p>{`Emacs has some awesome stuff going for it:`}</p>
    <ul>
      <li parentName="ul">{`Lines blurred between configuration and extension`}</li>
      <li parentName="ul">{`A declarative package manager, `}<inlineCode parentName="li">{`straight.el`}</inlineCode></li>
      <li parentName="ul">{`Evil, by far the best Vim emulation implementation in any editor`}</li>
      <li parentName="ul">{`Spacemacs-style keybinds are genius`}</li>
    </ul>
    <h2>{`The Bad`}</h2>
    <p>{`Over time, I ran into more and more issues that on their own were not bad, but over time added up. These is going to be the focus of this post.`}</p>
    <ul>
      <li parentName="ul">{`Performance on Windows is `}<em parentName="li">{`terrible`}</em>{`, partly due to synchronous packages`}</li>
      <li parentName="ul">{`The package ecosystem is very brittle`}</li>
    </ul>
    <p>{`By the end of my time with Emacs, I was depressed. I thought I had found the end-all editor, the last one I would ever use, and it would be infinitely customizable! In reality, it made me not want to program anymore.`}</p>
    <h3>{`Poor Performance`}</h3>
    <p>{`I ran Emacs on the fastest hardware I possibly could. A Ryzen 5800X, super fast memory, NVMe storage. I made one mistake here, though, which was running Windows.`}</p>
    <p>{`I had a long battle with startup performance. On my fairly nice laptop, Emacs took around 10 seconds to start up. I partially solved that by using an Emacs server, but still had issues with new frames taking a long time to open. I ran into issues with the server frequently enough that I bound a system-wide hotkey to kill and restart the server. It was not fun.`}</p>
    <p>{`As part of using the Emacs server, I also got comfortable with Doom Emacs' workspaces, which leverage persp-mode. I ran into all sorts of incompatibilities and weird bugs between various packages and persp-mode.`}</p>
    <p>{`When I built my desktop, I gave up the Emacs server and swallowed the startup times. It took around four seconds. This made it so that I couldn't comfortably use Emacs for, say, writing a Git commit message, so I became very familiar with `}<inlineCode parentName="p">{`git commit -m`}</inlineCode>{`.`}</p>
    <p>{`Performance issues plagued me in more places than in startup.`}</p>
    <p>{`Every Emacs user raves about Magit. It really is good, and the only tool that seemed like it could displace the Git CLI for me... except that it's super duper slow on Windows. I stopped trying to use it after a couple months.`}</p>
    <p>{`The LSP package had some sort of bad interaction with rust-analyzer, Rust's LSP server. Opening a Rust file would hang the editor for up to 5 seconds. `}</p>
    <h3>{`Brittle Package Ecosystem`}</h3>
    <ul>
      <li parentName="ul">{`TODO: Never upgraded Doom successfully, not once.`}</li>
      <li parentName="ul">{`TODO: Treemacs. Holy hell.`}</li>
      <li parentName="ul">{`TODO: persp-mode`}</li>
      <li parentName="ul">{`TODO: trying to turn on line numbers`}</li>
    </ul>
    <h2>{`The Now`}</h2>
    <p>{`I ended up switching back to Sublime Text. There were a lot of lessons to take away from using Emacs, though.`}</p>
    <p>{`I've upgraded to Sublime Text 4, which is even faster than 3 was. I learned the fundamentals of the Sublime Text plugin API and wrote a couple plugins. It turns out that's pretty easy.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
